import { useEffect, useRef, useState, useCallback } from 'react';
import {
    useMap,
} from '@vis.gl/react-google-maps';
import { MapMarker } from './elements/MapMarker';
import LocationIdbRepository from '../../../../repositories/idb/LocationIdbRepository';
import PilotAssignmentIdbRepository from '../../../../repositories/idb/PilotAssignmentIdbRepository';
import { isArrayEmpty, isNullOrEmpty, isObjectNull } from '../../../helpers/ObjectHelpers';
import MapPropertiesService from '../services/MapPropertiesService';
import MapApiRepository from '../repositories/MapApiRepository';
import {
    useShowLocations,
    useSelectLocation,
    useExcludeLocationIds,
    useMapBounds,
    useMapZoom,
    useMapDragEnd,
    useMapResize
} from '../hooks/useMapStore';

interface MapLocationsProps {
    pilotageId: number,
}

export const MapLocations = ({
    pilotageId,
}: MapLocationsProps) => {

    const map = useMap();

    const showLocations = useShowLocations();

    const [sourceLocations, setSourceLocations] = useState<any[]>([]);

    const initialize = useCallback(async () => {
        let locations = await LocationIdbRepository.getAllAsync();
        
        if (pilotageId > 0) {
            const pilotAssignment = await PilotAssignmentIdbRepository.getByPilotageIdAsync(pilotageId);
            if (!isObjectNull(pilotAssignment)) {
                const excludedIds = new Set([
                    pilotAssignment.pilotage.fromLocation.locationId,
                    pilotAssignment.pilotage.toLocation.locationId,
                ]);
                locations = locations.filter((l: any) => !excludedIds.has(l.locationId));
            }
        }
        setSourceLocations(locations);
    }, [pilotageId]);

    useEffect(() => {
        if (!map) return;

        if (!showLocations) {
            setSourceLocations([]);
        } else {
            initialize();
        }
    }, [map, showLocations]);

    useEffect(() => {
        return (() => {
          setSourceLocations([]);  
        })
    }, []);

    if(sourceLocations.length === 0 || !showLocations) return null;

    return (
        <MapLocationsContent
            pilotageId={pilotageId}
            sourceLocations={sourceLocations}
        />
    )
}

interface MapLocationsContentProps {
    sourceLocations: any[]
}

export const MapLocationsContent = ({
    sourceLocations
}: MapLocationsContentProps) => {

    const showLocations = useShowLocations();
    const stateSelectLocation = useSelectLocation();
    const excludeLocationIds = useExcludeLocationIds();
    const stateBounds = useMapBounds();
    const stateZoom = useMapZoom();
    const stateDragEnd = useMapDragEnd();
    const stateResize = useMapResize();

    const map = useMap();
    const markerRefs: { current: any[] } = useRef([]);

    const maxCount = 25;
    const mapMarkerTypes = MapPropertiesService.markerTypes;
    const locationTypes = MapPropertiesService.locationTypes;
    const mapProperties = MapPropertiesService.properties;

    const [currentZoom, setCurrentZoom] = useState(0);
    const [markers, setMarkers] = useState<any[]>([]);

    const componentRef = useRef({
        hasInitialized: false,
        isBusy: false,
        hasCancelled: false,
    });

    // Memoize location color mapping
    const getLocationColor = useCallback((systemName: string) => {
        switch (systemName) {
            case locationTypes.Harbour:
                return 'var(--map-pin-location-harbour-bg)';
            case locationTypes.Anchorage:
                return 'var(--map-pin-location-anchorage-bg)';
            case locationTypes.LocationAtSea:
                return 'var(--map-pin-location-location-at-sea-bg)';
            case locationTypes.PilotBoarding:
                return 'var(--map-pin-location-pilot-boarding-bg)';
            default:
                return 'var(--map-pin-location-default-bg)';
        }
    }, [locationTypes]);

    const handleOnSelect = useCallback(async (e: any) => {
        if (!map || !e) return;

        const location = await MapApiRepository.getLocationByIdAsync(e.locationId);
        if (!location) return;

        const latLng = new window.google.maps.LatLng(location.latitude, location.longitude);
        map.setCenter(latLng);
        map.setZoom(12);
    }, [map]);

    const onSetZoom = useCallback(() => {
        if (!map) return;

        const zoom = map.getZoom();
        if (zoom) {
            setCurrentZoom(zoom);
        }
    }, [map]);

    const getLocations = useCallback(async () => {
        if (!map || !showLocations) return;

        const zoom = map.getZoom();

        // Update marker visibility based on zoom
        for (const ref of markerRefs.current) {
            if (!isNullOrEmpty(ref)) {
                ref.onToggleVisibility(zoom);
            }
        }

        if (!zoom || zoom < mapProperties.MarkersVisibleAtZoom) return;

        if (componentRef.current.isBusy) return;
        componentRef.current.isBusy = true;

        const bounds = map.getBounds();

        if (!bounds) {
            componentRef.current.isBusy = false;
            return;
        }
        componentRef.current.hasCancelled = false;

        const dataMarkers: any[] = [];
        const markersToRemove: any[] = [];
        const excludedIdsSet = new Set(excludeLocationIds);

        for (const location of sourceLocations) {
            if (componentRef.current.hasCancelled) break;

            const existing = markers.filter(m => m.id === location.locationId);
            
            if (isArrayEmpty(existing)) {
                const latLng = new window.google.maps.LatLng(location.latitude, location.longitude);
                const locationBounds = new window.google.maps.LatLngBounds();
                locationBounds.extend(latLng);

                if (!bounds.intersects(locationBounds) || excludedIdsSet.has(location.locationId)) {
                    continue;
                }

                const marker = {
                    id: location.locationId,
                    description: location.name,
                    isEligibleForSpecialPilotageFromTimeLockDeadline:
                        location.isEligibleForSpecialPilotageFromTimeLockDeadline,
                    position: latLng,
                    type: mapMarkerTypes.Location,
                    color: getLocationColor(location.systemName),
                    currentZoom: zoom,
                    topmost: true,
                };

                dataMarkers.push(marker);
            } else if (excludedIdsSet.has(location.locationId)) {
                markersToRemove.push(...existing);
            }
        }

        if (!componentRef.current.hasCancelled) {
            setMarkers(prev => {
                const idsToRemove = new Set(markersToRemove.map(m => m.id));
                return [...prev.filter(m => !idsToRemove.has(m.id)), ...dataMarkers];
            });
        }

        componentRef.current.hasInitialized = true;
        componentRef.current.isBusy = false;

        onSetZoom();
    }, [
        map,
        sourceLocations,
        markers,
        excludeLocationIds,
        mapProperties,
        mapMarkerTypes,
        getLocationColor,
        showLocations,
        onSetZoom
    ]);

    useEffect(() => {
        return (() => {
            componentRef.current.hasInitialized = false;
            componentRef.current.isBusy = false;
            componentRef.current.hasCancelled = true;
            setMarkers([]);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [visibleCount, setVisibleCount] = useState(maxCount);
    useEffect(() => {
        if (markers.length > visibleCount) {
            const timeout = setTimeout(() => {
                setVisibleCount(prev => Math.min(prev + maxCount, markers.length));
            }, 50); // 50ms pause between batches
            return () => clearTimeout(timeout);
        }
    }, [markers, visibleCount]);
        
    useEffect(() => {
        handleOnSelect(stateSelectLocation);
    }, [stateSelectLocation]);
    
    useEffect(() => {
        componentRef.current.hasCancelled = true;
        getLocations();
    }, [stateBounds, stateZoom, stateDragEnd, stateResize, excludeLocationIds]);
    
    return (
        markers.slice(0, visibleCount).map((marker, index) =>
            <MapMarker
                ref={(e: any) => markerRefs.current[index] = e}
                key={index}
                marker={marker}
                currentZoom={currentZoom}
            />
        )
    )
}
